/**
 * continuityCoordinator.js
 *
 * Master coordinator for the Longitudinal Spiritual Memory + Pastoral Continuity layer.
 * Produces ONE compact [CONTINUIDAD PASTORAL] system prompt block.
 *
 * Answers: "What does Kevia need to remember about this person's JOURNEY
 * (not just today) to respond as a pastor who truly knows them?"
 *
 * DISTINCT FROM:
 *   [relationship: ...]  — short-term visit/tone context (7-day window)
 *   [trajectory: ...]    — trajectory snapshot (restaurando/distanciamiento)
 * THIS LAYER ADDS:
 *   Temporal arc across weeks/months, pattern recognition, regression/restoration
 *   adjustments, milestone-aware recall, continuity sensitivity filtering.
 *
 * Signal gate: omitted when:
 *   — no journey history AND no milestones
 *   — or recallMode = "silent"
 *
 * Max block: 550 chars.
 *
 * Design:
 *  — Pure function after storage reads; no async, no side effects
 *  — Consumes assessment.emotion, FASE 8 judgment outputs, discernment/formation
 *  — All 6 subordinate modules run; only contextualRecallEngine decides what to include
 */

import { getJourneyHistory }              from "./spiritualJourneyMemory";
import { getMilestones, buildMilestonesInstruction } from "./spiritualMilestoneTracker";
import { detectPastoralPattern }          from "./pastoralPatternRecognition";
import { resolveTemporalArc }             from "./longitudinalDiscernmentEngine";
import { detectRegressionRestoration }    from "./regressionAndRestorationDetector";
import { assessContinuitySensitivity }    from "./continuitySensitivityEngine";
import { filterContextualRecall }         from "./contextualRecallEngine";

const MAX_CONTINUITY_CHARS = 550;

/**
 * @param {{
 *   entityId:           string|null,
 *   entityType:         string|null,
 *   text:               string,
 *   intent:             string,
 *   emotion:            object|null,          — from assessment.emotion
 *   riskLevel:          string,
 *   processState:       string,               — from FASE 8 judgment.processState
 *   complexity:         string,               — from FASE 8 judgment.complexity
 *   trajectoryStage:    string,               — from kevia-relationships trajectory
 *   discernmentTiming:  string,               — from FASE 6 discernment.timing
 *   formationPresence:  string,               — from FASE 7 formation.presence
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   continuityContext: string|null,
 *   pattern:           string,
 *   journeyArc:        string,
 *   recallMode:        string,
 *   milestoneCount:    number,
 *   regressionState:   string,
 *   isActive:          boolean,
 * }}
 */
export function buildContinuityContext({
  entityId           = null,
  entityType         = null,
  text               = "",
  intent             = "general",
  emotion            = null,
  riskLevel          = "none",
  processState       = "apertura",
  complexity         = "simple",
  trajectoryStage    = "desconocido",
  discernmentTiming  = "respond_now",
  formationPresence  = "acompañante",
  conversationLength = 0,
}) {
  const nullResult = {
    continuityContext: null,
    pattern:           "sin_patron",
    journeyArc:        "sin_historia",
    recallMode:        "silent",
    milestoneCount:    0,
    regressionState:   "stable",
    isActive:          false,
  };

  // No entity = no longitudinal memory to consult
  if (!entityId) return nullResult;

  // ── Load stored journey and milestones ────────────────────────────────────
  const journeyRecord = getJourneyHistory(entityId);
  const milestones    = getMilestones(entityId);
  const hasHistory    = !!journeyRecord;
  const hasMilestones = milestones.length > 0;

  // ── Early exit: nothing stored ────────────────────────────────────────────
  if (!hasHistory && !hasMilestones) return nullResult;

  // ── Run all modules ───────────────────────────────────────────────────────
  const patternResult    = detectPastoralPattern(journeyRecord);
  const arcResult        = resolveTemporalArc({ currentState: processState, journeyRecord, trajectoryStage });
  const regressionResult = detectRegressionRestoration(journeyRecord, processState);
  const sensitivityResult = assessContinuitySensitivity({
    hasJourneyHistory:  hasHistory,
    hasMilestones,
    conversationLength,
    emotion,
    processState,
    regressionType:     regressionResult.type,
    intent,
  });

  // ── Signal gate ───────────────────────────────────────────────────────────
  if (sensitivityResult.recallDepth === "none") {
    return {
      ...nullResult,
      pattern:         patternResult.pattern,
      journeyArc:      arcResult.arc,
      recallMode:      sensitivityResult.mode,
      milestoneCount:  milestones.length,
      regressionState: regressionResult.type,
    };
  }

  const milestoneInstruction = buildMilestonesInstruction(milestones);

  const { recallLines, recallActive } = filterContextualRecall({
    recallMode:              sensitivityResult.mode,
    recallDepth:             sensitivityResult.recallDepth,
    arc:                     arcResult.arc,
    temporalContext:         arcResult.temporalContext,
    pattern:                 patternResult.pattern,
    patternDescription:      patternResult.description,
    milestones,
    regressionInstruction:   regressionResult.instruction,
    milestoneInstruction,
    sensitivityInstruction:  sensitivityResult.instruction,
    processState,
    emotion,
  });

  if (!recallActive || recallLines.length === 0) {
    return {
      ...nullResult,
      pattern:         patternResult.pattern,
      journeyArc:      arcResult.arc,
      recallMode:      sensitivityResult.mode,
      milestoneCount:  milestones.length,
      regressionState: regressionResult.type,
    };
  }

  const block = recallLines.join("\n").slice(0, MAX_CONTINUITY_CHARS);

  return {
    continuityContext: block,
    pattern:           patternResult.pattern,
    journeyArc:        arcResult.arc,
    recallMode:        sensitivityResult.mode,
    milestoneCount:    milestones.length,
    regressionState:   regressionResult.type,
    isActive:          true,
  };
}
